import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, ArrowRight } from "lucide-react";
import { ABOUT_IMAGE, COMPANY, VALUES } from "@/lib/data";
import MeasurementDivider from "@/components/MeasurementDivider";

export default function UeberUns() {
  return (
    <div data-testid="ueber-uns-page">
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-messinger-bg overflow-hidden border-b border-white/5">
        <div className="absolute inset-x-0 top-0 h-[50%] bg-gradient-to-b from-messinger-orange/10 to-transparent pointer-events-none" />
        <div className="container-x relative">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-messinger-orange" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-messinger-orange">
              Über uns
            </span>
          </div>
          <h1 className="font-heading font-semibold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl text-balance">
            Persönlich. Direkt. <br />
            <span className="text-messinger-orange">Mit Leidenschaft fürs Auto.</span>
          </h1>
        </div>
      </section>

      <section className="bg-messinger-surface py-24 lg:py-32 relative">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 items-start relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-messinger-orange" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-messinger-orange" />
              <div className="relative overflow-hidden bg-messinger-blue aspect-[4/5]">
                <img src={ABOUT_IMAGE} alt="Mike Messinger Portrait" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="mt-6 surface-card p-6 rounded-2xl">
              <div className="font-heading font-semibold text-white text-lg">
                {COMPANY.owner}
              </div>
              <div className="text-sm text-white/55 mt-1">{COMPANY.role}</div>
              <div className="mt-4 pt-4 border-t border-white/10 text-sm text-white/70">
                {COMPANY.address.street}
                <br />
                {COMPANY.address.zip} {COMPANY.address.city}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <Quote size={36} className="text-messinger-orange mb-6" />
            <h2 className="font-heading font-semibold text-white text-3xl sm:text-4xl leading-tight text-balance">
              „Ein Gutachten ist mehr als ein Stück Papier — es ist Vertrauen
              in schriftlicher Form.“
            </h2>
            <div className="mt-8 space-y-5 text-white/80 leading-relaxed text-base lg:text-lg">
              <p>
                Mein Name ist Mike Messinger. Seit über zwei Jahrzehnten dreht
                sich bei mir alles ums Auto — zuerst als KFZ-Meister in der
                Werkstatt, später als unabhängiger KFZ-Sachverständiger. Diese
                Mischung aus echtem Handwerk und prüferischem Blick ist heute
                der Kern meiner Arbeit.
              </p>
              <p>
                Als Einzelunternehmer aus Castrop-Rauxel betreue ich Sie
                persönlich — vom ersten Anruf bis zur Übergabe des Gutachtens.
                Kein Callcenter. Kein anonymer Sachbearbeiter. Sie wissen
                jederzeit, mit wem Sie sprechen — und vor allem, woran Sie
                sind.
              </p>
              <p>
                Für Sie bedeutet das: schnelle Terminvergabe, transparente
                Kommunikation und Gutachten, die vor Versicherung und Gericht
                bestehen. Mein Anspruch ist nicht, möglichst viele Aufträge zu
                bearbeiten — sondern jedes Gutachten so präzise, sorgfältig
                und verständlich zu erstellen, dass Sie sich später sagen:
                Das war die richtige Entscheidung.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <MeasurementDivider label="Werte" />

      <section className="bg-messinger-bg py-20 lg:py-28 relative">
        <div className="container-x relative">
          <div className="max-w-2xl mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-messinger-orange" />
              <span className="label-eyebrow">Was uns trägt</span>
            </div>
            <h2 className="font-heading font-semibold text-white text-3xl sm:text-4xl leading-tight">
              Drei Werte — gelebt, nicht plakatiert.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="surface-card p-8 rounded-2xl hover:border-messinger-orange/50 transition-colors"
                data-testid={`value-${i}`}
              >
                <div className="font-heading text-5xl font-bold text-messinger-orange/40 leading-none">
                  0{i + 1}
                </div>
                <h3 className="mt-4 font-heading font-semibold text-white text-xl">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">
                  {v.text}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 flex">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.14em] uppercase text-white hover:text-messinger-orange transition-colors"
              data-testid="ueber-uns-cta"
            >
              Termin vereinbaren <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
